import { useState, useEffect, useRef } from "react";
import { CampLocation } from "@/types/campLocation";
import { db } from "@/services/firebaseService";
import { getCampLocationById } from "@/services/campLocationService";

/**
 * Hook to ensure we're using the published version of a location
 * @param location The location to verify
 * @returns The published location (or the provided one if it's already published)
 */
export const usePublishedLocation = (location: CampLocation) => {
  const [verifiedLocation, setVerifiedLocation] = useState<CampLocation>(location);
  const locationLoadedRef = useRef(false);
  
  // Verify location is published and fetch directly if needed - run only once
  useEffect(() => {
    // Skip if already processed
    if (locationLoadedRef.current) return;
    
    const verifyPublishedLocation = async () => {
      // If location is already published, use it
      if (location.visibility === "v_published") {
        setVerifiedLocation(location);
        locationLoadedRef.current = true;
        return;
      }
      
      // Otherwise fetch the published version directly
      try {
        const fetchedLocation = await getCampLocationById(db, location.id);
        if (fetchedLocation && fetchedLocation.visibility === "v_published") {
          setVerifiedLocation(fetchedLocation);
        } else {
          console.error("[usePublishedLocation] Failed to fetch published location or location is not published!");
        }
        // Mark as loaded regardless of result to prevent repeat attempts
        locationLoadedRef.current = true;
      } catch (error) {
        console.error("[usePublishedLocation] Error fetching published location:", error);
        locationLoadedRef.current = true;
      }
    };
    
    verifyPublishedLocation();
  }, [location.id, location.visibility]);

  return {
    verifiedLocation,
    isLocationLoaded: locationLoadedRef.current,
  };
};
